import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import {
  Brain,
  TrendingUp,
  Wallet,
  Eye,
  GraduationCap,
  LayoutDashboard,
  AlertTriangle,
  Check,
} from "lucide-react";

import handLoggerImg from "@/assets/landing/hand-logger.png";
import statisticsImg from "@/assets/landing/statistics.png";
import bankrollImg from "@/assets/landing/bankroll.png";
import opponentsImg from "@/assets/landing/opponents.png";
import coachesImg from "@/assets/landing/coaches.png";
import coachDashboardImg from "@/assets/landing/coach-dashboard.png";

const stagger = {
  visible: { transition: { staggerChildren: 0.15 } },
};

const featureShowcase = [
  {
    title: "Hand Logger",
    problem: "Playing on feel.",
    solution:
      "Log every hand at the table — manually or by voice. Our visual poker table makes it fast, and the AI tells you exactly where you deviated from GTO.",
    image: handLoggerImg,
    icon: Brain,
  },
  {
    title: "Statistics",
    problem: "No idea if you're winning.",
    solution:
      "See cumulative profit, hourly rate, win rate, ROI, and session breakdowns at a glance. Spot leaks in your game before they drain your bankroll.",
    image: statisticsImg,
    icon: TrendingUp,
  },
  {
    title: "Bankroll Management",
    problem: "No bankroll discipline.",
    solution:
      "Track every deposit, withdrawal, and session result. Auto-sync with your sessions so your bankroll updates itself. See your high/low watermarks and monthly trends.",
    image: bankrollImg,
    icon: Wallet,
  },
  {
    title: "Opponent Scouting",
    problem: "You don't know who you're playing against.",
    solution:
      "Track VPIP, PFR, aggression factor, and tendencies for every opponent. Tag them as fish, nit, LAG — and know exactly how to exploit them.",
    image: opponentsImg,
    icon: Eye,
  },
  {
    title: "Coaching Marketplace",
    problem: "No feedback loop.",
    solution:
      "Browse verified coaches, book sessions on your schedule, and leave reviews. Level up with personalized coaching instead of guessing alone.",
    image: coachesImg,
    icon: GraduationCap,
  },
  {
    title: "Coach Dashboard",
    problem: "Coaches can't find students.",
    solution:
      "If you're a coach, get discovered by serious players. Track your bookings, earnings, reviews, and client progress — all in one place.",
    image: coachDashboardImg,
    icon: LayoutDashboard,
  },
];

function FeatureBlock({
  feature,
  index,
}: {
  feature: (typeof featureShowcase)[0];
  index: number;
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const isEven = index % 2 === 0;

  const imageVariant = {
    hidden: { opacity: 0, x: isEven ? -60 : 60 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.7, ease: "easeOut" as const },
    },
  };

  const textVariant = {
    hidden: { opacity: 0, x: isEven ? 60 : -60 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.7, ease: "easeOut" as const, delay: 0.15 },
    },
  };

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={stagger}
      className={`flex flex-col ${isEven ? "md:flex-row" : "md:flex-row-reverse"} items-center gap-8 md:gap-12`}
    >
      {/* Screenshot */}
      <motion.div variants={imageVariant} className="w-full md:w-1/2">
        <motion.div
          whileHover={{ scale: 1.03 }}
          transition={{ type: "spring", stiffness: 300, damping: 20 }}
          className="relative rounded-lg overflow-hidden border border-border/50 shadow-2xl"
        >
          <div className="absolute inset-0 bg-gradient-to-t from-primary/5 to-transparent pointer-events-none z-10" />
          <img
            src={feature.image}
            alt={`${feature.title} screenshot`}
            className="w-full h-auto"
            loading="lazy"
          />
        </motion.div>
      </motion.div>

      {/* Text */}
      <motion.div variants={textVariant} className="w-full md:w-1/2 space-y-4">
        <div className="flex items-center gap-3">
          <motion.div
            initial={{ rotate: 0 }}
            animate={inView ? { rotate: 360 } : { rotate: 0 }}
            transition={{ duration: 0.6, delay: 0.3, ease: "easeOut" }}
            className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"
          >
            <feature.icon className="h-5 w-5 text-primary" />
          </motion.div>
          <h3 className="font-display text-xl font-bold uppercase tracking-wider text-foreground">
            {feature.title}
          </h3>
        </div>

        <div className="flex items-start gap-2 text-destructive">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <p className="text-sm font-semibold italic">{feature.problem}</p>
        </div>

        <div className="flex items-start gap-2 text-primary">
          <Check className="h-4 w-4 mt-0.5 shrink-0" />
          <p className="text-sm text-muted-foreground leading-relaxed">{feature.solution}</p>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function FeatureShowcase() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });

  return (
    <section ref={ref} className="py-20 px-4">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={stagger}
          className="text-center mb-16"
        >
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-wide mb-4"
          >
            Your Unfair Advantage
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="text-muted-foreground max-w-2xl mx-auto"
          >
            Six powerful tools working together so you can stop guessing and start crushing.
          </motion.p>
        </motion.div>

        <div className="space-y-20">
          {featureShowcase.map((feature, i) => (
            <FeatureBlock key={feature.title} feature={feature} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
